import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import type { JWTTokenModel } from '@r/api/generated/client.schemas.ts';
import { triggerServerError } from '@r/lib/server-error-events.ts';

const APP_ID = (import.meta.env as { VITE_APP_ID?: string } | undefined)?.VITE_APP_ID ?? '';
const BASE_URL = (import.meta.env as { VITE_BASE_URL?: string } | undefined)?.VITE_BASE_URL ?? '';
const axiosInstance: AxiosInstance = axios.create({
  baseURL: BASE_URL
});

let isRefreshing = false;
let refreshSubscribers: ((token: string | null) => void)[] = [];

const addSubscriber = (callback: (token: string | null) => void) => {
  refreshSubscribers.push(callback);
};

const onRefreshComplete = (newToken: string | null) => {
  for (const callback of refreshSubscribers) {
    callback(newToken);
  }
  refreshSubscribers = [];
};

type RetriableAxiosRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
};

interface AxiosErrorResponse {
  config?: RetriableAxiosRequestConfig;
  response?: {
    status?: number;
  };
}

const AUTH_ERROR_STATUS_CODES = new Set([401, 403]);

const isAuthError = (error: AxiosErrorResponse): boolean => {
  const status = error?.response?.status;
  return typeof status === 'number' && AUTH_ERROR_STATUS_CODES.has(status);
};

const isAuthEndpointRequest = (config?: RetriableAxiosRequestConfig): boolean => {
  const url = config?.url;

  if (!url) {
    return false;
  }

  const pathname = url.startsWith('http') ? new URL(url).pathname : url.split('?')[0];

  return pathname === '/auth' || pathname.startsWith('/auth/');
};

const setAuthorizationHeader = (config: RetriableAxiosRequestConfig, token: string | null) => {
  config.headers ??= {};
  config.headers.Authorization = token ? `Bearer ${token}` : undefined;
};

async function updateRefreshToken(): Promise<JWTTokenModel> {
  if (typeof window === 'undefined') {
    throw new Error('Token refresh not available in Node.js environment');
  }
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token found');
  }

  try {
    const response = await axios.post<JWTTokenModel>(
      `${BASE_URL}/auth/refresh`,
      { refreshToken },
      {
        headers: {
          'App-ID': APP_ID
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Token refresh failed:', error);
    throw error;
  }
}

if (typeof window !== 'undefined') {
  axiosInstance.interceptors.request.use(
    (config) => {
      const token: string | null = localStorage.getItem('token');

      config.headers['App-ID'] = APP_ID;

      if (token && !isAuthEndpointRequest(config)) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      if (error?.response?.status === 503) {
        triggerServerError('503');
        return Promise.reject(error);
      }

      const refreshToken: string | null = localStorage.getItem('refreshToken');
      const originalRequest = error.config;

      if (isAuthError(error) && originalRequest && !isAuthEndpointRequest(originalRequest)) {
        if (!refreshToken) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          if (originalRequest.headers?.Authorization) {
            setAuthorizationHeader(originalRequest, null);
            return axiosInstance(originalRequest);
          }
          return Promise.reject(error);
        }

        if (!originalRequest._retry) {
          originalRequest._retry = true;

          if (isRefreshing) {
            return new Promise((resolve) => {
              addSubscriber((token: string | null) => {
                setAuthorizationHeader(originalRequest, token);
                resolve(axiosInstance(originalRequest));
              });
            });
          }

          isRefreshing = true;

          try {
            const data = await updateRefreshToken();

            localStorage.setItem('token', data.token);
            localStorage.setItem('refreshToken', data.refreshToken);
            axiosInstance.defaults.headers.common.Authorization = `Bearer ${data.token}`;

            onRefreshComplete(data.token);

            setAuthorizationHeader(originalRequest, data.token);
            return axiosInstance(originalRequest);
          } catch (refreshError) {
            console.error('Failed to refresh token:', refreshError);

            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');

            onRefreshComplete(null);

            setAuthorizationHeader(originalRequest, null);
            return axiosInstance(originalRequest);
          } finally {
            isRefreshing = false;
          }
        }
      }

      return Promise.reject(error);
    }
  );
}

export default axiosInstance;
